import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaPlay, FaPause, FaSlidersH } from 'react-icons/fa';
import { getOverlayPrefs, setOverlayPref, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { MdReplay10, MdForward10 } from 'react-icons/md';
import { SPEEDS } from '../hooks/useTimelinePlayhead';
import LayoutGrid, { confGlyph } from '../live/LayoutGrid';
import { isMobilePortrait } from '../live/dragViewport';
import Sheet from '../common/Sheet';

/**
 * Transport — the playback control bar. Three composable clusters:
 *   [Layout]  |  [Prev][«10s][Play/Pause][10s»][Next]  elapsed  [Speed▾]  |  [Bulk][Cog]
 *              └ 'layout'        └ 'playback'                                  └ 'options'
 *
 * `parts` (default all three, in this order) selects which clusters render. The DEFAULT renders the exact
 * same DOM as before, so the mounts that compose the whole bar (the landscape rail + the legacy tiers) are
 * unchanged. The Option-A portrait layout (#86bb7phhh S20) mounts SUBSETS in different rows — `['playback']`
 * inside the timeline card, `['layout']` + `['options']` on the banks row — so the clusters relocate without
 * duplicating control logic or diverging behavior (each mount is the same component). `className` lets a
 * mount restyle its container (e.g. drop the pill for a bare banks-row button).
 *
 * The ±10s fine-skip flanks Play where the row has width (CSS-gated >=768px; mobile-portrait keeps just the
 * clip-jump pair). Prev/Next jump CLIP-to-clip. Menus are portaled bottom sheets (ride the viewport bottom).
 * Presentational: the parent owns the playhead.
 */
const SPEED_LABELS = { 0.25: '¼x', 0.5: '½x', 1: '1x', 2: '2x', 4: '4x' };
const ALL_PARTS = ['layout', 'playback', 'options'];

const Transport = ({
  playing, speed, elapsed, total, inline = false, hidden = false, bulkSlot = null, layout, onLayout,
  autoSkip, onToggleAutoSkip, videoInfo = false, onToggleVideoInfo,
  quickActionsAlways = false, onToggleQuickActions,
  detectionOverlaysAvailable = false,
  onPlayPause, onSpeed, onSkipBack, onSkipFwd,
  parts = ALL_PARTS, className = '',
}) => {
  const [menu, setMenu] = useState(null); // 'speed' | 'cog' | 'layout' | null — one popover open at a time
  const ref = useRef(null);
  const [ovl, setOvl] = useState(getOverlayPrefs);
  useEffect(() => subscribeOverlayPrefs(setOvl), []);
  const has = (p) => parts.includes(p);

  const pickSpeed = (s) => { onSpeed(s); setMenu(null); };

  // Portal a transport option-menu bottom sheet to the .mcplayback-container so it rides the VIEWPORT
  // bottom, escaping this transport's transformed/absolute box (David 2026-08-04). Falls back inline (tests).
  const portalSheet = (node) => {
    const host = ref.current && ref.current.closest('.mcplayback-container');
    return host ? ReactDOM.createPortal(node, host) : node;
  };

  return (
    <div
      className={`mcpb-transport${inline ? ' mcpb-transport--row' : ''}${hidden ? ' mcpb-transport--hidden' : ''}${className ? ` ${className}` : ''}`}
      ref={ref}
      /* The 'transport' (play/skip/speed/time) is the playback cluster — keep the canonical testid on it
         (and on the full bar, which includes playback). Bare layout/options mounts get suffixed testids so
         a portrait page has exactly one `mcpb-transport`. #86bb7phhh S20. */
      data-testid={has('playback') ? 'mcpb-transport' : `mcpb-transport-${parts.join('-')}`}
    >
      {/* Grid layout picker — FAR LEFT (David 2026-08-04). Icon-only accurate per-conf glyph (shared
          confGlyph); opens the LayoutGrid bottom sheet (PORTALED, see the return tail). */}
      {has('layout') && onLayout && (
        <div className="mcpb-transport__layout">
          <button
            className="mcpb-layout-trigger"
            type="button"
            title={`${layout}-up layout`}
            aria-label="Grid layout"
            aria-haspopup="dialog"
            aria-expanded={menu === 'layout'}
            data-layout={layout}
            onClick={() => setMenu(menu === 'layout' ? null : 'layout')}
          >
            {confGlyph(`conf-${layout}`, isMobilePortrait())}
            {/* Conf number beside the glyph — like Live's grid button. CSS gates its visibility per context. */}
            <span className="mcpb-layout-trigger__num" aria-hidden="true">{layout}</span>
          </button>
        </div>
      )}

      {has('playback') && (
        <>
          {/* Clip-nav (⏮/⏭) removed (David #86bb7phhh S25): in multicam the per-cam clip boundaries are
              ambiguous and usually arbitrary file splits — tap-to-seek + the event/gap bands + the WhenButton
              cover navigation. ±10s stays: a precise, predictable nudge, now shown on ALL tiers for one
              consistent transport (↺ ▶ ↻ everywhere). Seek handled by the parent. */}
          {onSkipBack && (
            <button className="mcpb-transport__btn mcpb-transport__btn--skip" type="button" title="Back 10s" aria-label="Skip back 10 seconds" onClick={onSkipBack}>
              <MdReplay10 />
            </button>
          )}
          <button
            className="mcpb-transport__btn mcpb-transport__btn--play"
            type="button"
            title={playing ? 'Pause' : 'Play'}
            aria-label={playing ? 'Pause' : 'Play'}
            aria-pressed={playing}
            onClick={onPlayPause}
          >
            {playing ? <FaPause /> : <FaPlay />}
          </button>
          {onSkipFwd && (
            <button className="mcpb-transport__btn mcpb-transport__btn--skip" type="button" title="Forward 10s" aria-label="Skip forward 10 seconds" onClick={onSkipFwd}>
              <MdForward10 />
            </button>
          )}

          <span className="mcpb-transport__divider" aria-hidden="true" />
          <span className="mcpb-transport__elapsed">{elapsed}{total ? ` / ${total}` : ''}</span>

          {/* Speed trigger — opens the Playback-speed bottom sheet (PORTALED, see the return tail). */}
          <div className="mcpb-transport__speed">
            <button
              className="mcpb-speed-trigger"
              type="button"
              title="Playback speed"
              aria-label="Playback speed"
              aria-haspopup="dialog"
              aria-expanded={menu === 'speed'}
              onClick={() => setMenu(menu === 'speed' ? null : 'speed')}
            >
              {SPEED_LABELS[speed] || `${speed}x`}
            </button>
          </div>
        </>
      )}

      {/* View cluster — bulk-actions + cog. display:contents below desktop; absolutely right-pinned at
          >=1280 so the controls dead-center in the full bar (David 2026-06-24). */}
      {has('options') && (
        <div className="mcpb-transport__viewcluster">
          {/* Bulk-actions menu (conf>1). conf=1 passes null. */}
          <span className="mcpb-transport__rightcluster">{bulkSlot}</span>

          {/* Cog trigger — opens the Options bottom sheet (PORTALED, see the return tail). */}
          <div className="mcpb-transport__cog">
            <button
              className="mcpb-cog-trigger"
              type="button"
              title="Options"
              aria-label="Options"
              aria-haspopup="dialog"
              aria-expanded={menu === 'cog'}
              onClick={() => setMenu(menu === 'cog' ? null : 'cog')}
            >
              <FaSlidersH />
            </button>
          </div>
        </div>
      )}

      {/* Transport option-menu bottom sheets — all PORTALED to .mcplayback-container so they ride the
          VIEWPORT bottom. One shared scrim; the open `menu` selects the sheet. Triggers live up in the row. */}
      {menu && portalSheet(
        <>
          {menu === 'layout' && onLayout && (
            <Sheet className="mcpb-sheet mcpb-sheet--layout" scrimClassName="mcpb-sheet-scrim" title="Layout" ariaLabel="Grid layout" onClose={() => setMenu(null)}>
              <LayoutGrid conf={`conf-${layout}`} onSelect={(c) => { onLayout(Number(c.replace('conf-', ''))); setMenu(null); }} />
            </Sheet>
          )}

          {menu === 'speed' && (
            <Sheet className="mcpb-sheet mcpb-sheet--speed" scrimClassName="mcpb-sheet-scrim" title="Playback speed" ariaLabel="Playback speed" onClose={() => setMenu(null)}>
              <div className="mcpb-sheet__seg" role="group" aria-label="Playback speed">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`mcpb-sheet__seg-btn${s === speed ? ' mcpb-sheet__seg-btn--active' : ''}`}
                    aria-pressed={s === speed}
                    onClick={() => pickSpeed(s)}
                  >
                    {SPEED_LABELS[s]}
                  </button>
                ))}
              </div>
            </Sheet>
          )}

          {menu === 'cog' && (
            <Sheet className="mcpb-sheet mcpb-sheet--settings" scrimClassName="mcpb-sheet-scrim" title="Options" ariaLabel="Options" onClose={() => setMenu(null)}>
              {onToggleAutoSkip && (
                <label className="mcpb-cog-popover__row" title="Skip over gaps with no recorded video">
                  <span className="mcpb-cog-popover__row-label">Auto skip gaps</span>
                  <span className="mcpb-switch">
                    <input className="mcpb-switch__input" type="checkbox" aria-label="Auto skip gaps" checked={!!autoSkip} onChange={onToggleAutoSkip} />
                    <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
                  </span>
                </label>
              )}
              {onToggleVideoInfo && (
                <label className="mcpb-cog-popover__row" title="Show resolution, FPS, and stream profile on each camera">
                  <span className="mcpb-cog-popover__row-label">Camera Info overlay</span>
                  <span className="mcpb-switch">
                    <input className="mcpb-switch__input" type="checkbox" aria-label="Camera Info overlay" checked={!!videoInfo} onChange={onToggleVideoInfo} />
                    <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
                  </span>
                </label>
              )}
              {detectionOverlaysAvailable && (
                <>
                  <label className="mcpb-cog-popover__row" title="Draw boxes and labels around detected objects">
                    <span className="mcpb-cog-popover__row-label">Object Detection</span>
                    <span className="mcpb-switch">
                      <input className="mcpb-switch__input" type="checkbox" aria-label="Object Detection boxes" checked={!!ovl.objects} onChange={() => setOverlayPref('objects', !ovl.objects)} />
                      <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
                    </span>
                  </label>
                  <label className="mcpb-cog-popover__row" title="Draw license-plate boxes and plate text">
                    <span className="mcpb-cog-popover__row-label">License Plates</span>
                    <span className="mcpb-switch">
                      <input className="mcpb-switch__input" type="checkbox" aria-label="License Plate boxes" checked={!!ovl.lpr} onChange={() => setOverlayPref('lpr', !ovl.lpr)} />
                      <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
                    </span>
                  </label>
                </>
              )}
              {onToggleQuickActions && (
                <label className="mcpb-cog-popover__row" title="Always show the Snapshot / Download / Archive / Export buttons on every camera (off = reveal on hover or tap)">
                  <span className="mcpb-cog-popover__row-label">Always-show quick actions</span>
                  <span className="mcpb-switch">
                    <input className="mcpb-switch__input" type="checkbox" aria-label="Always-show quick actions" checked={!!quickActionsAlways} onChange={onToggleQuickActions} />
                    <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
                  </span>
                </label>
              )}
            </Sheet>
          )}
        </>
      )}
    </div>
  );
};

export default Transport;
